import { AvailabilityEmptyState } from '@/features/account-availability/availability-empty-state'
import { GeneralSettings } from '@/features/account-availability/general-settings'
import { Overrides } from '@/features/account-availability/overrides'
import { SaveBar } from '@/features/account-availability/save-bar'
import { useAvailabilityForm } from '@/features/account-availability/use-availability-form'
import { WeekCalendarPreview } from '@/features/account-availability/week-calendar-preview'
import { WeeklySchedule } from '@/features/account-availability/weekly-schedule'
import { ReconnectCalendarBanner } from '@/ui/core/reconnect-calendar-banner'
import { useConfirmedBookingsIndex } from '@jobilla/nexus-sdk'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_auth/account/availability')({
	component: RouteComponent,
})

function RouteComponent() {
	const { t } = useTranslation('common')
	const {
		form,
		isLoading,
		isLoadError,
		isFirstTimeSetup,
		isDirty,
		savedRecently,
		isPending,
		validationError,
		hasGenericError,
		updateForm,
		addSlot,
		applyDefaultWorkingWeek,
		removeSlot,
		updateSlot,
		addOverride,
		removeOverride,
		updateOverride,
		handleSave,
	} = useAvailabilityForm()

	const {
		data: bookingsData,
		isLoading: isBookingsLoading,
		isError: isBookingsError,
	} = useConfirmedBookingsIndex({ staleTime: 60_000 })

	if (isLoading) {
		return (
			<div className="flex items-center justify-center min-h-64 text-sm text-grey-400">
				{t('account.availability.loading')}
			</div>
		)
	}

	if (isLoadError) {
		return (
			<div className="flex items-center justify-center min-h-64 text-sm text-red-500">
				{t('account.availability.load_error')}
			</div>
		)
	}

	if (isFirstTimeSetup) {
		return <AvailabilityEmptyState onGetStarted={applyDefaultWorkingWeek} />
	}

	return (
		<div className="flex h-full overflow-hidden">
			<div className="w-[clamp(20vw,28rem,40vw)] shrink-0 overflow-y-auto max-h-full border-r border-grey-100 px-8 py-10 flex flex-col gap-8">
				<div>
					<h1 className="text-2xl font-semibold text-grey-900">
						{t('account.availability.title')}
					</h1>
					<p className="text-sm text-grey-500 mt-1">
						{t('account.availability.description')}
					</p>
				</div>

				<ReconnectCalendarBanner />

				<GeneralSettings form={form} updateForm={updateForm} />
				<WeeklySchedule
					form={form}
					addSlot={addSlot}
					removeSlot={removeSlot}
					updateSlot={updateSlot}
				/>
				<Overrides
					form={form}
					addOverride={addOverride}
					removeOverride={removeOverride}
					updateOverride={updateOverride}
				/>
				<SaveBar
					isDirty={isDirty}
					savedRecently={savedRecently}
					isPending={isPending}
					validationError={validationError}
					hasGenericError={hasGenericError}
					onSave={handleSave}
				/>
			</div>

			<div className="flex-1 overflow-y-auto bg-grey-50/50 flex items-start justify-center p-10">
				<div className="w-full max-w-2xl">
					<WeekCalendarPreview
						form={form}
						confirmedBookings={bookingsData?.data}
						bookingsLoading={isBookingsLoading}
						bookingsError={isBookingsError}
					/>
				</div>
			</div>
		</div>
	)
}
